import cloneDeep from 'clone-deep'
import { useEffect, useState } from 'react'
import { filterUserRoles, getApiKeyScopes } from 'services'
import { Modal, SearchableSelect } from 'stories/components'
import { InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { defaultInputs } from './constants'
import { API_KEY_SCOPE_ALL, IApiKeyScope } from './type'

/**
 * Create form for an API key. `onClose(payload)` receives the new key's fields on submit, or
 * `null` when cancelled. The parent owns persistence and the one-time reveal that follows.
 *
 * Access is chosen here rather than defaulted. The server still treats an omitted `scopes` as full
 * access, for the keys issued before scopes were enforced, but a key minted from this screen
 * always states what it may reach: full access is a checkbox an admin ticks on purpose, not what
 * they get by saying nothing.
 *
 * Which account the key runs as is chosen here too, and cannot be changed afterwards: there is no
 * endpoint to rebind a key, by design, since moving a live credential to another account silently
 * changes what it reaches.
 */
export const CreateApiKeyModal = ({ onClose }: { onClose: Function }) => {
  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState(defaultInputs())
  const [catalogue, setCatalogue] = useState<IApiKeyScope[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [fullAccess, setFullAccess] = useState(false)
  const [scopeError, setScopeError] = useState('')
  // A string because that is what the picker speaks; empty means no account, which is the reach
  // every key issued before this field existed has.
  const [runAsUserId, setRunAsUserId] = useState('')

  useEffect(() => {
    let live = true
    getApiKeyScopes()
      .then((data: IApiKeyScope[]) => {
        if (live) setCatalogue(Array.isArray(data) ? data : [])
      })
      .catch(() => {
        // A key can still be issued with full access if the catalogue cannot be read, which is
        // better than a modal that refuses to work; the checkbox below is then the only choice.
        if (live) setCatalogue([])
      })
    return () => {
      live = false
    }
  }, [])

  const onChange = (key: string, value: any) => {
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = value
    newInputs[key].error = ''
    setInputs(newInputs)
  }

  const toggleScope = (scope: string) => {
    setScopeError('')
    setSelected((current) => (current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope]))
  }

  const onSubmit = async () => {
    const newInputs = cloneDeep(inputs)
    const values: Record<string, any> = {}
    let hasError = false
    Object.keys(inputs).forEach((key) => {
      newInputs[key].error = InputValidate(newInputs[key])
      const { value, error } = newInputs[key]
      if (error) hasError = true
      values[key] = value
    })
    setInputs(newInputs)

    const scopes = fullAccess ? [API_KEY_SCOPE_ALL] : selected
    if (!scopes.length) {
      setScopeError('Choose what this key may reach, or tick full access.')
      hasError = true
    }
    if (hasError) return false

    setLoading(true)
    await onClose({
      name: values.name,
      description: values.description || '',
      scopes,
      // The date input yields a plain day; the API wants an instant. End-of-day UTC rather than
      // end-of-day local, so the date the admin picked is the date the list shows back - a local
      // instant renders a day later for any viewer east of the one who created it.
      expiresAt: values.expiresAt ? `${values.expiresAt}T23:59:59.999Z` : undefined,
      runAsUserId: runAsUserId ? Number(runAsUserId) : undefined,
    })
    setLoading(false)
  }

  return (
    <Modal isOpen loading={isLoading} title="Create API Key" titleOkay="Create" onClose={() => onClose(null)} onOk={onSubmit}>
      <div className="w-128">
        <p className="text-sm text-gray-500 mb-4">
          The key is shown once, immediately after it is created, and cannot be retrieved afterwards. If it is lost,
          revoke it and issue a new one.
        </p>
        {Object.keys(inputs).map((key) => {
          const input = inputs[key]
          if (input.visible === false) return null

          return (
            <div className="mb-4" key={key}>
              <RenderInput Key={key} input={input} onChange={onChange} />
            </div>
          )
        })}

        <div className="mb-5">
          <p className="text-sm font-medium text-gray-900">Runs as</p>
          <p className="text-xs text-gray-500 mb-2">
            The account this key acts as. Give it one and the key reaches whatever that account can, within the access
            chosen below, and the audit trail names the account rather than nothing. Leave it blank and the key reaches
            only the endpoints built for integrations; the rest of the API refuses it.
          </p>
          <SearchableSelect
            title="Account"
            value={runAsUserId}
            options={(query: string) => filterUserRoles(`${query}`.trim())}
            hasDefaultOption
            defaultOptionText="No account (integration endpoints only)"
            sort
            onChange={(value: string) => setRunAsUserId(value)}
          />
          <div className="flex items-start justify-between gap-3 mt-1">
            <p className="text-xs text-gray-400">
              Search by name, company or email. This cannot be changed once the key is created, and the key stops
              working if the account is deactivated or deleted.
            </p>
            {!!runAsUserId && (
              <span className="text-xs text-shade-blue cursor-pointer hover:underline shrink-0" onClick={() => setRunAsUserId('')}>
                Clear
              </span>
            )}
          </div>
        </div>

        <div className="mb-2">
          <p className="text-sm font-medium text-gray-900">Access</p>
          <p className="text-xs text-gray-500 mb-2">
            Grant only what the integration needs. A key that can read an accounting feed does not need to run an
            import or delete a loan.
          </p>

          <label className="flex items-start gap-2 mb-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 cursor-pointer"
              checked={fullAccess}
              onChange={() => {
                setScopeError('')
                setFullAccess(!fullAccess)
              }}
            />
            <span className="text-sm text-gray-700">
              Full access <span className="text-gray-500">(every external endpoint, including imports and deletion)</span>
            </span>
          </label>

          {!fullAccess && (
            <div className="pl-1">
              {catalogue.map(({ scope, label }) => (
                <label className="flex items-start gap-2 mb-1 cursor-pointer" key={scope}>
                  <input
                    type="checkbox"
                    className="mt-0.5 cursor-pointer"
                    checked={selected.includes(scope)}
                    onChange={() => toggleScope(scope)}
                  />
                  <span className="text-sm text-gray-700">
                    {label} <span className="text-gray-400">{scope}</span>
                  </span>
                </label>
              ))}
              {!catalogue.length && (
                <p className="text-xs text-gray-400">
                  The scope list could not be loaded. Tick full access above, or close and try again.
                </p>
              )}
            </div>
          )}

          {!!scopeError && <p className="text-xs text-red-600 mt-1">{scopeError}</p>}
        </div>
      </div>
    </Modal>
  )
}
